<template>
  <div class="contact-view pa-6">
    <v-fade-transition mode="out-in">
      <div v-if="loading" key="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      
      <div v-else-if="portfolio" key="content">
        <div v-if="hasContactInfo">
          <h2 class="text-h4 font-weight-black mb-8 d-flex align-center">
            <v-icon icon="mdi-at" class="mr-3" color="primary" size="large"></v-icon>
            Connect & Contact
          </h2>

          <!-- Social Media Grid -->
          <div v-if="portfolio.socials?.length" class="mb-10">
            <div class="text-overline text-grey mb-4 font-weight-bold">Social Profiles</div>
            <v-row>
              <v-col v-for="(social, index) in portfolio.socials" :key="index" cols="12" sm="6" md="4">
                <v-card 
                  variant="outlined" 
                  class="social-card rounded-xl border border-white-05 bg-black-05"
                  :href="social.url"
                  target="_blank"
                >
                  <v-card-text class="d-flex align-center py-4">
                    <v-avatar color="primary-lighten-4" size="40" class="mr-4">
                      <v-icon :icon="getSocialIcon(social.platform)" color="primary"></v-icon>
                    </v-avatar>
                    <div class="flex-grow-1 min-width-0">
                      <div class="text-subtitle-1 font-weight-black text-white text-truncate">{{ social.platform }}</div>
                      <div class="text-caption text-grey text-truncate">{{ social.url }}</div>
                    </div>
                    <v-icon icon="mdi-open-in-new" size="small" color="grey-darken-1" class="ml-2"></v-icon>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Direct Contact -->
          <div v-if="portfolio.contact?.email || portfolio.contact?.website" class="contact-details">
            <div class="text-overline text-grey mb-4 font-weight-bold">Direct Channels</div>
            
            <v-list class="bg-transparent pa-0">
              <v-list-item 
                v-if="portfolio.contact?.email" 
                class="rounded-xl border border-white-05 bg-black-05 mb-4 px-6 py-4"
                :href="'mailto:' + portfolio.contact.email"
              >
                <template v-slot:prepend>
                  <v-icon icon="mdi-email-outline" color="primary" class="mr-4"></v-icon>
                </template>
                <v-list-item-title class="text-h6 font-weight-bold text-white">{{ portfolio.contact.email }}</v-list-item-title>
                <v-list-item-subtitle class="text-grey">Send an email</v-list-item-subtitle>
              </v-list-item>

              <v-list-item 
                v-if="portfolio.contact?.website" 
                class="rounded-xl border border-white-05 bg-black-05 px-6 py-4"
                :href="portfolio.contact.website"
                target="_blank"
              >
                <template v-slot:prepend>
                  <v-icon icon="mdi-web" color="primary" class="mr-4"></v-icon>
                </template>
                <v-list-item-title class="text-h6 font-weight-bold text-white">{{ cleanUrl(portfolio.contact.website) }}</v-list-item-title>
                <v-list-item-subtitle class="text-grey">Personal Website</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
        </div>

        <div v-else class="text-center py-12 border rounded-xl border-dashed border-white-05 opacity-50">
          <v-icon icon="mdi-account-off-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
          <h3 class="text-h6 text-grey">No contact information provided</h3>
          <p class="text-caption text-grey mt-2">The user hasn't shared any external links or contact methods yet.</p>
        </div>
      </div>

      <div v-else-if="!loading" key="error" class="text-center py-12">
        <v-icon icon="mdi-alert-circle-outline" size="48" color="error" class="mb-4"></v-icon>
        <p class="text-grey">Could not load contact details.</p>
      </div>
    </v-fade-transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { getPortfolio, Portfolio } from '@/services/bluesky';

const props = defineProps<{
  userDid?: string;
  isOwnProfile?: boolean;
}>();

const portfolio = ref<Portfolio | null>(null);
const loading = ref(true);

const hasContactInfo = computed(() => {
  if (!portfolio.value) return false;
  return (portfolio.value.socials?.length || 0) > 0 || 
         !!portfolio.value.contact?.email || 
         !!portfolio.value.contact?.website;
});

const getSocialIcon = (platform: string) => {
  const p = platform.toLowerCase();
  if (p.includes('twitter') || p.includes('x')) return 'mdi-twitter';
  if (p.includes('github')) return 'mdi-github';
  if (p.includes('linkedin')) return 'mdi-linkedin';
  if (p.includes('youtube')) return 'mdi-youtube';
  if (p.includes('instagram')) return 'mdi-instagram';
  if (p.includes('facebook')) return 'mdi-facebook';
  if (p.includes('twitch')) return 'mdi-twitch';
  if (p.includes('discord')) return 'mdi-discord';
  return 'mdi-link-variant';
};

const cleanUrl = (url: string) => {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

const refresh = async () => {
  if (!props.userDid) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    portfolio.value = await getPortfolio(props.userDid);
  } catch (error) {
    console.error("Failed to load contact info:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(refresh);
watch(() => props.userDid, refresh);

defineExpose({ refresh });
</script>

<style scoped>
.contact-view {
  animation: fadeIn 0.4s ease-out;
  max-width: 900px;
  margin: 0 auto;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.social-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.social-card:hover {
  transform: translateY(-4px);
  border-color: rgba(var(--v-theme-primary), 0.5) !important;
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}

.bg-black-05 {
  background: rgba(255, 255, 255, 0.03) !important;
  backdrop-filter: blur(10px);
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.border-dashed {
  border-style: dashed !important;
}

.min-width-0 {
  min-width: 0;
}
</style>
